import type { BindingAnchor, BoardObjectV2 } from "@bezpart-board/shared";
import type { SceneObject } from "../SceneHost";
import type { PathPoint } from "../pathGeometry";
import { hitTestTopObject } from "../HitTest";
import { anchorPointOnObject, isBindableObject } from "./resolveBindings";
import { objectBounds } from "../selectionBounds";

export const BIND_SNAP_RADIUS = 28;

export function findBindableAtPoint(
  objects: SceneObject[],
  x: number,
  y: number,
  excludeIds?: Set<string>
): SceneObject | null {
  const hit = hitTestTopObject(objects, x, y, BIND_SNAP_RADIUS);
  if (!hit || !isBindableObject(hit)) return null;
  if (excludeIds?.has(hit.id)) return null;
  return hit;
}

export function inferAutoBinding(
  target: SceneObject,
  toward: PathPoint
): { objectId: string; anchor: BindingAnchor } {
  return {
    objectId: target.id,
    anchor: "auto",
  };
}

export function snapPointToBindable(
  objects: BoardObjectV2[],
  point: PathPoint,
  toward: PathPoint,
  excludeIds?: Set<string>
): { point: PathPoint; binding: { objectId: string; anchor: BindingAnchor } } | null {
  const hit = findBindableAtPoint(objects, point[0], point[1], excludeIds);
  if (!hit) return null;
  const b = objectBounds(hit);
  const inside =
    point[0] > b.x && point[0] < b.x + b.w && point[1] > b.y && point[1] < b.y + b.h;
  if (inside) {
    // Конец внутри фигуры. Раньше он прилипал к середине стороны, ближней
    // к ДРУГОМУ концу, — у диагонали ромба оба конца садились в одну точку,
    // и линия пропадала. Теперь внутри цепляемся только к точке крепления
    // рядом с самим концом (вершина ромба, середина стороны), а иначе конец
    // остаётся там, где его поставили.
    let best: { anchor: BindingAnchor; pt: PathPoint; d: number } | null = null;
    for (const anchor of ["top", "right", "bottom", "left"] as const) {
      const pt = anchorPointOnObject(hit, anchor);
      const d = Math.hypot(pt[0] - point[0], pt[1] - point[1]);
      if (d <= BIND_SNAP_RADIUS && (!best || d < best.d)) best = { anchor, pt, d };
    }
    if (!best) return null;
    return { point: best.pt, binding: { objectId: hit.id, anchor: best.anchor } };
  }
  const snapped = anchorPointOnObject(hit, "auto", toward);
  return {
    point: snapped,
    binding: inferAutoBinding(hit, toward),
  };
}

export const CONNECTOR_ANCHORS: BindingAnchor[] = [
  "top",
  "right",
  "bottom",
  "left",
  "center",
];

export function connectorAnchorLabel(anchor: BindingAnchor): string {
  switch (anchor) {
    case "top":
      return "Верх";
    case "bottom":
      return "Низ";
    case "left":
      return "Лево";
    case "right":
      return "Право";
    case "center":
      return "Центр";
    default:
      return anchor;
  }
}
